import { cloneDeep } from 'lodash'
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react'
import { cloneElement } from '../../elements/cloneElement'
import { duplicateElementTree, getIdsToDelete, repairGroups } from '../../elements/groups'
import { TemplateElement } from '../../templateEditor.types'

const MAX_HISTORY = 100

export type DocumentSnapshot = {
    elements: TemplateElement[]
    selectedIds: string[]
}

const snapshotOf = (elements: TemplateElement[], selectedIds: string[]): DocumentSnapshot => ({
    elements: cloneDeep(elements),
    selectedIds: [...selectedIds],
})

export const useTemplateEditorHistory = (
    elements: TemplateElement[],
    selectedIds: string[],
    setElements: Dispatch<SetStateAction<TemplateElement[]>>,
    setSelectedIds: Dispatch<SetStateAction<string[]>>
) => {
    const pastRef = useRef<DocumentSnapshot[]>([])
    const futureRef = useRef<DocumentSnapshot[]>([])
    const clipboardRef = useRef<TemplateElement[]>([])
    const skipHistoryRef = useRef(false)
    const pendingPointerSnapshotRef = useRef<DocumentSnapshot | null>(null)

    const elementsRef = useRef(elements)
    const selectedIdsRef = useRef(selectedIds)
    elementsRef.current = elements
    selectedIdsRef.current = selectedIds

    const [canUndo, setCanUndo] = useState(false)
    const [canRedo, setCanRedo] = useState(false)

    const syncHistoryFlags = useCallback(() => {
        setCanUndo(pastRef.current.length > 0)
        setCanRedo(futureRef.current.length > 0)
    }, [])

    const applySnapshot = useCallback(
        (snapshot: DocumentSnapshot) => {
            skipHistoryRef.current = true
            setElements(cloneDeep(snapshot.elements))
            setSelectedIds([...snapshot.selectedIds])
            skipHistoryRef.current = false
        },
        [setElements, setSelectedIds]
    )

    const pushHistory = useCallback(() => {
        if (skipHistoryRef.current) {
            return
        }

        pastRef.current.push(snapshotOf(elementsRef.current, selectedIdsRef.current))
        if (pastRef.current.length > MAX_HISTORY) {
            pastRef.current.shift()
        }
        futureRef.current = []
        syncHistoryFlags()
    }, [syncHistoryFlags])

    const undo = useCallback(() => {
        const previous = pastRef.current.pop()
        if (!previous) {
            return false
        }

        futureRef.current.push(snapshotOf(elementsRef.current, selectedIdsRef.current))
        applySnapshot(previous)
        syncHistoryFlags()
        return true
    }, [applySnapshot, syncHistoryFlags])

    const redo = useCallback(() => {
        const next = futureRef.current.pop()
        if (!next) {
            return false
        }

        pastRef.current.push(snapshotOf(elementsRef.current, selectedIdsRef.current))
        applySnapshot(next)
        syncHistoryFlags()
        return true
    }, [applySnapshot, syncHistoryFlags])

    const mutateElements = useCallback(
        (updater: (prev: TemplateElement[]) => TemplateElement[], nextSelectedIds?: string[]) => {
            pushHistory()
            setElements(updater)
            if (nextSelectedIds !== undefined) {
                setSelectedIds(nextSelectedIds)
            }
        },
        [pushHistory, setElements, setSelectedIds]
    )

    const beginPointerInteraction = useCallback(() => {
        pendingPointerSnapshotRef.current = snapshotOf(elementsRef.current, selectedIdsRef.current)
    }, [])

    const endPointerInteraction = useCallback(() => {
        const pending = pendingPointerSnapshotRef.current
        pendingPointerSnapshotRef.current = null

        if (!pending || skipHistoryRef.current) {
            return
        }

        pastRef.current.push(pending)
        if (pastRef.current.length > MAX_HISTORY) {
            pastRef.current.shift()
        }
        futureRef.current = []
        syncHistoryFlags()
    }, [syncHistoryFlags])

    const copySelection = useCallback(() => {
        const selected = elementsRef.current.filter((item) => selectedIdsRef.current.includes(item.id))
        if (selected.length === 0) {
            return false
        }

        clipboardRef.current = cloneDeep(selected)
        return true
    }, [])

    const pasteClipboard = useCallback(() => {
        if (clipboardRef.current.length === 0) {
            return false
        }

        const clones = clipboardRef.current.map((element, index) => cloneElement(element, 20 * (index + 1)))

        pushHistory()
        setElements((prev) => [...prev, ...clones])
        setSelectedIds(clones.map((clone) => clone.id))
        return true
    }, [pushHistory, setElements, setSelectedIds])

    const deleteSelection = useCallback(() => {
        if (selectedIdsRef.current.length === 0) {
            return false
        }

        const idsToRemove = getIdsToDelete(elementsRef.current, selectedIdsRef.current)
        pushHistory()
        setElements((prev) => repairGroups(prev.filter((element) => !idsToRemove.has(element.id))))
        setSelectedIds([])
        return true
    }, [pushHistory, setElements, setSelectedIds])

    const duplicateSelection = useCallback(() => {
        if (selectedIdsRef.current.length === 0) {
            return false
        }

        const { elements: next, newIds } = duplicateElementTree(elementsRef.current, selectedIdsRef.current, 10)
        pushHistory()
        setElements(next)
        setSelectedIds(newIds)
        return true
    }, [pushHistory, setElements, setSelectedIds])

    const clearHistory = useCallback(() => {
        pastRef.current = []
        futureRef.current = []
        pendingPointerSnapshotRef.current = null
        syncHistoryFlags()
    }, [syncHistoryFlags])

    return {
        canUndo,
        canRedo,
        undo,
        redo,
        mutateElements,
        pushHistory,
        clearHistory,
        beginPointerInteraction,
        endPointerInteraction,
        copySelection,
        pasteClipboard,
        deleteSelection,
        duplicateSelection,
    }
}

export type TemplateEditorHistoryApi = ReturnType<typeof useTemplateEditorHistory>
